import type { Transition, Variants } from "framer-motion";

/**
 * REGEL: Einzige Quelle für alle Bewegungswerte der Website.
 * Komponenten definieren keine eigenen Dauern, Easings oder Varianten mehr,
 * sondern importieren hier. So bleibt das Bewegungsbild überall identisch.
 *
 * Bildsprache: "Foto wird entwickelt" — aus leichter Tiefe und Unschärfe
 * ruhig in die Schärfe. Kein Hüpfen, kein Überschwingen.
 */

/** Weiches Auslaufen ohne Overshoot (ease-out-quint-artig). */
export const EASE_BRAND = [0.22, 1, 0.36, 1] as const;

export const MOTION_DURATION = {
  fast: 0.3,
  base: 0.7,
  slow: 1.0,
} as const;

/** Versatz zwischen Geschwistern einer Gruppe. */
export const STAGGER_STEP = 0.12;

export const baseTransition: Transition = {
  duration: MOTION_DURATION.base,
  ease: EASE_BRAND,
};

/** Standard-Eintritt: von unten, minimal größer, aus feiner Unschärfe. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 48, scale: 0.97, filter: "blur(10px)" },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: baseTransition,
  },
};

/** Reiner Einblender ohne Versatz — für großflächige Blöcke. */
export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: baseTransition },
};

/** Eintritt von der Seite — für Bild/Text-Paare. */
export const fadeSide = (from: "left" | "right"): Variants => ({
  hidden: { opacity: 0, x: from === "left" ? -56 : 56, filter: "blur(10px)" },
  visible: { opacity: 1, x: 0, filter: "blur(0px)", transition: baseTransition },
});

/** Elternvariante für gestaffelte Gruppen. */
export const staggerGroup = (step: number = STAGGER_STEP, delay = 0): Variants => ({
  hidden: {},
  visible: {
    transition: { staggerChildren: step, delayChildren: delay },
  },
});

/** Kindvariante innerhalb einer gestaffelten Gruppe. */
export const revealItem: Variants = fadeUp;

/** Einheitlicher Sichtbarkeits-Trigger: nur einmal, kurz vor dem Rand. */
export const viewportOnce = { once: true, margin: "-10% 0px -10% 0px" } as const;

/** Varianten ohne Bewegung — Fallback bei "Bewegung reduzieren". */
export const staticVariants: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1 },
};
